import {
  Alert,
  Badge,
  Button,
  Group,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconHistory,
  IconPlayerPause,
  IconPlayerPlay,
  IconSquareCheck,
  IconWifi,
  IconWifiOff,
} from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  DEMO_SCENARIO_LABELS,
  DEMO_SOLO_TASK_REWARDS,
  PLAYERS_COUNT_LABEL,
  RESOURCE_LABELS,
  type EventCardAward,
  type GameSessionView,
  type ResourceKey,
} from '@creativitas/shared';
import { useSession } from '@/features/auth/useSession';
import { SessionStatusBadge } from '@/components/domain/SessionStatusBadge';
import { PageHeader } from '@/components/ui/PageHeader';
import { ErrorState, LoadingState } from '@/components/ui/StateViews';
import { SessionParticipants } from '@/features/spreads/SessionParticipants';
import { HostSeatAction } from './HostSeatAction';
import { useSessionRealtime } from '@/features/spreads/useSessionRealtime';
import { toUserMessage } from '@/lib/api';
import {
  adjustGameMoney,
  adjustGameResource,
  closeSectorStory,
  completeGameSession,
  confirmSoloTask,
  drawGameCard,
  drawGrantedTalent,
  drawStartTalent,
  exchangeAtBazaar,
  openSectorStory,
  resolveEventCard,
  transferGameResource,
  fetchGameSession,
  gameSessionKey,
  pauseGameSession,
  removeGameParticipant,
  resumeGameSession,
  revealGameCard,
  rollGameDice,
  setGameEntryOpen,
  seatHostAtGameTable,
  startGameSession,
  undoGameAction,
} from './api';
import { InviteButton } from '@/components/domain/InviteButton';
import { GameWorkspace } from './GameWorkspace';
import { describeDemoStep, type DemoViewer } from './demo/demo-step';
import { newerSnapshot, roster } from './game-turn';

type Command =
  | { type: 'start' | 'pause' | 'resume' | 'complete' | 'undo' | 'close-story' }
  | { type: 'entry'; open: boolean }
  | { type: 'open-story'; sectorId: string }
  | { type: 'roll' | 'draw' | 'remove' | 'start-talent' | 'granted-talent'; participantId: string }
  | { type: 'transfer'; fromParticipantId: string; toParticipantId: string; resource: ResourceKey }
  | { type: 'bazaar'; participantId: string; resource: ResourceKey }
  | { type: 'resolve'; sessionCardId: string; award: EventCardAward }
  | { type: 'seat' }
  | { type: 'reveal'; cardId: string }
  | { type: 'confirm'; cardId: string }
  | { type: 'resource'; participantId: string; resource: ResourceKey; delta: -1 | 1 }
  | { type: 'money'; participantId: string; delta: number };

export function GameSessionPage() {
  const { sessionId = '' } = useParams();
  const queryClient = useQueryClient();
  const { user } = useSession();
  const query = useQuery({
    queryKey: gameSessionKey(sessionId),
    queryFn: () => fetchGameSession(sessionId),
    enabled: Boolean(sessionId),
  });
  const realtime = useSessionRealtime<GameSessionView>({
    sessionId,
    enabled: Boolean(sessionId),
    onSnapshot: (snapshot) => queryClient.setQueryData<GameSessionView>(gameSessionKey(sessionId), (current) => {
      const next = newerSnapshot(current, snapshot);
      return next === current ? current : { ...snapshot, inviteToken: current?.inviteToken ?? snapshot.inviteToken };
    }),
  });
  const command = useMutation({
    mutationFn: (input: Command) => {
      switch (input.type) {
        case 'start': return startGameSession(sessionId);
        case 'seat': return seatHostAtGameTable(sessionId);
        case 'entry': return setGameEntryOpen(sessionId, input.open);
        case 'pause': return pauseGameSession(sessionId);
        case 'resume': return resumeGameSession(sessionId);
        case 'complete': return completeGameSession(sessionId);
        case 'undo': return undoGameAction(sessionId);
        case 'open-story': return openSectorStory(sessionId, input.sectorId);
        case 'close-story': return closeSectorStory(sessionId);
        case 'start-talent': return drawStartTalent(sessionId, input.participantId);
        case 'granted-talent': return drawGrantedTalent(sessionId, input.participantId);
        case 'transfer': return transferGameResource(sessionId, input);
        case 'bazaar': return exchangeAtBazaar(sessionId, input);
        case 'resolve': return resolveEventCard(sessionId, input);
        case 'roll': return rollGameDice(sessionId, input.participantId);
        case 'draw': return drawGameCard(sessionId, input.participantId);
        case 'remove': return removeGameParticipant(sessionId, input.participantId);
        case 'reveal': return revealGameCard(sessionId, input.cardId);
        case 'confirm': return confirmSoloTask(sessionId, input.cardId);
        case 'resource': return adjustGameResource(sessionId, input);
        case 'money': return adjustGameMoney(sessionId, {
          participantId: input.participantId,
          delta: input.delta > 0 ? 100_000 : -100_000,
        });
      }
    },
    onSuccess: (snapshot, input) => {
      queryClient.setQueryData<GameSessionView>(gameSessionKey(sessionId), (current) => newerSnapshot(current, snapshot));
      /*
        Обратная связь сразу, а не в конце (ТЗ демо, п. 2.1): какое действие
        завершено и какой ресурс получен.
      */
      if (input.type === 'confirm') {
        const card = snapshot.drawnCards.find((item) => item.id === input.cardId);
        const reward = card?.sectorId ? DEMO_SOLO_TASK_REWARDS[card.sectorId] : undefined;
        notifications.show({
          color: 'teal',
          title: 'Задание выполнено',
          message: reward
            ? `+1 «${RESOURCE_LABELS[reward]}» — ресурс лёг на ваш луч к Солнцу.`
            : 'Действие карты завершено.',
        });
      }
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });

  if (query.isLoading) return <LoadingState label="Восстанавливаем игровую сессию..." />;
  if (query.isError || !query.data) return <ErrorState onRetry={() => void query.refetch()} />;
  const session = query.data;
  const scenario = session.demo?.scenario ?? null;
  const solo = scenario === 'solo';
  const demoViewer: DemoViewer | undefined = scenario
    ? { role: solo ? 'solo' : 'facilitator', participantId: session.hostSeatParticipantId }
    : undefined;
  /* Демо, закрытое раньше маршрута, — досрочное: ведущего стоит об этом спросить. */
  /*
    Завершённая партия через час удаляется (политика, пп. 2.6, 6.5) — ведущий
    должен знать это до нажатия, а не узнать из плашки после.
  */
  const complete = () => {
    modals.openConfirmModal({
      title: scenario ? 'Завершить демопартию?' : 'Завершить партию?',
      children: (
        <Text size="sm" c="var(--text-muted)">
          {scenario ? 'Итог покажет только то, что участники успели получить. ' : ''}
          Продолжить эту партию будет нельзя. Поле, карты и журнал можно будет посмотреть ещё час,
          затем партия удаляется: архив игровых сессий не ведётся.
        </Text>
      ),
      labels: { confirm: 'Завершить', cancel: 'Продолжить игру' },
      onConfirm: () => command.mutate({ type: 'complete' }),
    });
  };
  return (
    <>
      <PageHeader
        eyebrow={scenario ? `Демоверсия · ${DEMO_SCENARIO_LABELS[scenario]}` : 'Игровая сессия'}
        title={session.game.title}
        description={
          <Group gap="xs">
            <SessionStatusBadge status={session.status} />
            <Badge color={realtime === 'connected' ? 'teal' : 'gold'} variant="light">
              {realtime === 'connected' ? <IconWifi size={13} /> : <IconWifiOff size={13} />} {' '}
              {realtime === 'connected' ? 'В сети' : 'Переподключение'}
            </Badge>
          </Group>
        }
        actions={
          <Group gap="xs">
            <Button component={Link} to={`/games/${session.game.slug}`} variant="subtle">К описанию</Button>
            {/*
              Приглашение — здесь, рядом с остальными действиями ведущего, а не
              панелью над столом: нужно оно один раз, а место занимало всегда.
            */}
            {session.status !== 'completed' && session.inviteToken ? (
              <span data-coach="invite">
                <InviteButton
                  inviteUrl={`${window.location.origin}/join/${session.inviteToken}`}
                  entryOpen={session.entryOpen}
                  busy={command.isPending}
                  onToggle={(open) => command.mutate({ type: 'entry', open })}
                />
              </span>
            ) : null}
            {/*
              Сесть можно и в идущую партию — как и войти по ссылке. Ведущий,
              открывший стол в расчёте на людей, не должен из-за этого остаться
              без собственной фишки. Пока он за столом, кнопки нет.

              В комнате ожидания её здесь тоже нет: там она стоит рядом с
              «Начать партию», где ведущий и решает, с кем начинает.
            */}
            {!solo &&
            session.status !== 'waiting' &&
            session.status !== 'completed' &&
            session.hostSeatParticipantId === null ? (
              <HostSeatAction
                variant="subtle"
                busy={command.isPending}
                onClick={() => command.mutate({ type: 'seat' })}
              />
            ) : null}
            {/* Одиночное демо ведёт система: служебных кнопок ведущего там нет. */}
            {solo ? null : session.status === 'active' ? (
              <Button variant="light" leftSection={<IconPlayerPause size={16} />} onClick={() => command.mutate({ type: 'pause' })}>Пауза</Button>
            ) : session.status === 'paused' ? (
              <Button variant="light" leftSection={<IconPlayerPlay size={16} />} onClick={() => command.mutate({ type: 'resume' })}>Продолжить</Button>
            ) : null}
            {!solo && (session.status === 'active' || session.status === 'paused') ? (
              <>
                <Button
                  variant="subtle"
                  leftSection={<IconHistory size={16} />}
                  disabled={!session.canUndo || command.isPending}
                  onClick={() => command.mutate({ type: 'undo' })}
                >
                  Отменить действие
                </Button>
                <Button
                  variant="light"
                  leftSection={<IconSquareCheck size={16} />}
                  onClick={complete}
                >
                  Завершить
                </Button>
              </>
            ) : null}
          </Group>
        }
      />

      {session.status === 'waiting' ? (
        <WaitingGameRoom
          session={session}
          busy={command.isPending}
          onStart={() => command.mutate({ type: 'start' })}
          onSeat={() => command.mutate({ type: 'seat' })}
          demoHint={demoViewer ? describeDemoStep(session, demoViewer) : null}
        />
      ) : (
        <GameWorkspace
          session={session}
          /*
            В одиночном демо владелец партии — игрок, а не ведущий: служебные
            кнопки ему не показываются, свои карты он открывает сам.
          */
          facilitator={!solo}
          viewerParticipantId={solo ? session.hostSeatParticipantId ?? undefined : undefined}
          busy={command.isPending}
          demoViewer={demoViewer}
          coachKey={`creativitas.demo-coach.${user?.id ?? 'anon'}.${scenario ?? 'full'}`}
          connection={realtime}
          actions={{
            start: () => command.mutate({ type: 'start' }),
            seat: () => command.mutate({ type: 'seat' }),
            confirm: (cardId) => command.mutate({ type: 'confirm', cardId }),
            openStory: (sectorId) => command.mutate({ type: 'open-story', sectorId }),
            closeStory: () => command.mutate({ type: 'close-story' }),
            startTalent: (participantId) => command.mutate({ type: 'start-talent', participantId }),
            roll: (participantId) => command.mutate({ type: 'roll', participantId }),
            draw: (participantId) => command.mutate({ type: 'draw', participantId }),
            reveal: (cardId) => command.mutate({ type: 'reveal', cardId }),
            resource: (participantId, resource, delta) => command.mutate({ type: 'resource', participantId, resource, delta }),
            money: (participantId, delta) => command.mutate({ type: 'money', participantId, delta }),
            remove: (participantId) => command.mutate({ type: 'remove', participantId }),
            transfer: (fromParticipantId, toParticipantId, resource) =>
              command.mutate({ type: 'transfer', fromParticipantId, toParticipantId, resource }),
            bazaar: (participantId, resource) =>
              command.mutate({ type: 'bazaar', participantId, resource }),
            grantedTalent: (participantId) => command.mutate({ type: 'granted-talent', participantId }),
            resolveCard: (sessionCardId, award) => command.mutate({ type: 'resolve', sessionCardId, award }),
          }}
        />
      )}
    </>
  );
}

function WaitingGameRoom({
  session,
  busy,
  onStart,
  onSeat,
  demoHint = null,
}: {
  session: GameSessionView;
  busy: boolean;
  onStart: () => void;
  onSeat: () => void;
  /** Демо: что делать в пустой или собранной комнате — словами, а не только кнопками. */
  demoHint?: { headline: string; detail: string } | null;
}) {
  /*
    Экран ожидания стал коротким: список участников и кнопка старта.

    Полосы «комната подключена» здесь больше нет — работающая связь в
    объявлении не нуждается, — а приглашение переехало в шапку. Партию можно
    открыть и за пустым столом: люди подтягиваются по ссылке уже внутри, и
    когда начать, решает ведущий, а не запрет в коде.
  */
  const { people: participants, seats } = roster(session);

  return (
    <Stack gap="lg">
      {demoHint ? (
        <Alert color="teal" variant="light" title={demoHint.headline} data-testid="demo-waiting-hint">
          {demoHint.detail}
        </Alert>
      ) : null}
      <SessionParticipants participants={participants} count={seats} countLabel={PLAYERS_COUNT_LABEL} />
      {/* По верхнему краю: под кнопкой ведущего есть пояснение, и по центру остальное съезжало. */}
      <Group justify="flex-end" gap="sm" align="flex-start">
        <Text size="sm" c="var(--text-muted)">
          Порядок хода — порядок входа. Опоздавшие встанут в конец очереди.
        </Text>
        {/*
          Ведущий может играть и сам. Отдельного одиночного режима для этого нет:
          он просто занимает место за собственным столом — и ведёт, и играет.
        */}
        {session.hostSeatParticipantId === null ? (
          <HostSeatAction busy={busy} onClick={onSeat} />
        ) : null}
        <Button
          leftSection={<IconPlayerPlay size={17} />}
          loading={busy}
          onClick={onStart}
        >
          Начать партию
        </Button>
      </Group>
    </Stack>
  );
}

